import React from 'react';
import { 
  Globe, Users, Clock, Zap, Shield, BookOpen,
  Accessibility, HeartHandshake, MessageSquare,
  UserMinus, Languages, Puzzle, Brain,
  Mail, Sparkles, Calendar, Eye, Globe2
} from 'lucide-react';
import Brand from '../components/Brand';

const ProductOnePager = () => {
  return (
    <div className="w-[8.5in] max-w-full h-[11in] mx-auto bg-white print:m-0 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24" />
        
        <div className="relative z-10">
          <Brand theme="dark" markClassName="text-white" className="text-3xl font-bold mb-4" />
          
          <h1 className="text-3xl font-bold mb-3">Let's Transform Your Resident Services</h1>
          <p className="text-lg text-blue-50 max-w-4xl">
            <strong className="text-white">HousingAI</strong> empowers Public Housing Authorities (PHAs) to advance fair housing by providing accessible, inclusive, and responsive chatbot technology designed specifically to enhance resident support and reduce systemic barriers.
          </p>
        </div>
        
        {/* Quick Start Badge */}
        <div className="absolute top-6 right-6 bg-green-500 text-white px-5 py-3 rounded-full font-bold shadow-lg transform rotate-3">
          <div className="text-sm">Get Started in</div>
          <div className="text-2xl">3 DAYS</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        {/* Why Choose Section */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-blue-600" />
            Why Choose HousingAI?
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4 border-2 border-blue-200">
              <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-lg mx-auto">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Access</h3>
              <p className="text-gray-700 text-center">Multilingual support ensuring inclusive communication for all residents.</p>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4 border-2 border-green-200">
              <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-lg mx-auto">
                <HeartHandshake className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Inclusivity</h3>
              <p className="text-gray-700 text-center">Tailored responses to meet diverse resident needs.</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4 border-2 border-purple-200">
              <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-lg mx-auto">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Responsiveness</h3>
              <p className="text-gray-700 text-center">24/7 instant resident assistance, reducing administrative load.</p>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
            <Zap className="h-8 w-8 text-green-600" />
            Features
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
              <div className="flex items-start gap-3">
                <UserMinus className="h-8 w-8 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1 text-sm">Reduced Staff Demand</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">The Chatbot answers high volume common questions, freeing up staff time for complex responsibilities.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
              <div className="flex items-start gap-3">
                <Accessibility className="h-8 w-8 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Accessibility</h4>
                  <p className="text-xs text-gray-600">Fully compliant with WCAG 2.1 AA standards, making digital assistance available to everyone.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
              <div className="flex items-start gap-3">
                <BookOpen className="h-8 w-8 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Housing Knowledge</h4>
                  <p className="text-xs text-gray-600">Pre-loaded with thousands of housing-specific topics and quick resolutions.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
              <div className="flex items-start gap-3">
                <Globe2 className="h-8 w-8 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Multilingual Support</h4>
                  <p className="text-xs text-gray-600">Offers assistance in 108 languages, ensuring broad accessibility and understanding.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
              <div className="flex items-start gap-3">
                <Puzzle className="h-8 w-8 text-teal-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Easy Integration</h4>
                  <p className="text-xs text-gray-600">Seamlessly integrates with your existing website infrastructure.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
              <div className="flex items-start gap-3">
                <Brain className="h-8 w-8 text-pink-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Continuous Learning</h4>
                  <p className="text-xs text-gray-600">Adapts and improves over time, staying at the forefront of AI capabilities.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="text-center mt-6">
          <div className="flex items-center justify-center gap-4 mb-3">
            <h3 className="text-xl font-bold text-gray-900">Contact Us Today:</h3>
            <div className="flex flex-wrap items-center gap-2">
              <Mail className="h-6 w-6 text-blue-600" />
              <span className="text-xl font-semibold text-blue-600">sales@gethousingai.com</span>
            </div>
          </div>
          <p className="text-lg text-gray-700 italic">Let's advance fair housing together!</p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white p-4 text-center mt-auto">
        <Brand theme="dark" markClassName="text-blue-600" className="justify-center text-xl font-bold mb-2" />
        <p className="text-gray-300">© 2026 HousingAI. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ProductOnePager;